import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Package, DollarSign, Hash, BarChart } from 'lucide-react';
import { rentalApi, fairApi } from '../services/api';
import { RentalItem } from '../types';
import RentalItemForm from './RentalItemForm';
import RentalStatsCard from './RentalStatsCard';
import { showError } from '../utils/toast';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

const CATEGORY_OPTIONS = [
  { value: 'furniture', label: 'Furniture', emoji: '🪑' },
  { value: 'lighting', label: 'Lighting', emoji: '💡' },
  { value: 'audio', label: 'Audio', emoji: '🔊' },
  { value: 'display', label: 'Display', emoji: '🖼️' },
  { value: 'power', label: 'Power', emoji: '🔌' },
  { value: 'tent', label: 'Tent/Canopy', emoji: '⛺' },
  { value: 'other', label: 'Other', emoji: '📦' },
];

export default function RentalItemsManager() {
  const { fairId: fairIdParam } = useParams<{ fairId: string }>();
  const fairId = fairIdParam ? parseInt(fairIdParam) : 0;

  const [items, setItems] = useState<RentalItem[]>([]);
  const [fairName, setFairName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<RentalItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showStats, setShowStats] = useState(false);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Load fair details
  useEffect(() => {
    const loadFair = async () => {
      if (!fairId) return;
      try {
        const fair = await fairApi.getById(fairId);
        setFairName(fair.name);
      } catch (err: unknown) {
        console.error('Failed to load fair details:', err);
        setError('Failed to load fair details');
      }
    };
    loadFair();
  }, [fairId]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const params: { category?: string; include_inactive?: boolean } = {
        include_inactive: includeInactive,
      };
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      const response = await rentalApi.getFairRentalItems(fairId, params);
      setItems(response.items);
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to load rental items:', err);
      setError('Failed to load rental items. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fairId, selectedCategory, includeInactive]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleCreateItem = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEditItem = (item: RentalItem) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingItem(null);
    loadItems();
  };

  const handleDeleteItem = async (itemId: number) => {
    try {
      await rentalApi.deleteRentalItem(itemId);
      setDeleteConfirm(null);
      loadItems();
    } catch (err: unknown) {
      console.error('Failed to delete rental item:', err);
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to delete item. It may have existing reservations.';
      showError(errorMessage);
      setDeleteConfirm(null);
    }
  };

  const getCategoryEmoji = (category?: string) => {
    const cat = CATEGORY_OPTIONS.find((c) => c.value === category);
    return cat?.emoji || '📦';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rental Equipment</h2>
          <p className="text-gray-600">Manage rental items for {fairName}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <BarChart className="w-5 h-5" />
            {showStats ? 'Hide Stats' : 'Show Stats'}
          </button>
          <button
            onClick={handleCreateItem}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="w-5 h-5" />
            Add Rental Item
          </button>
        </div>
      </div>

      {/* Stats Card */}
      {showStats && <RentalStatsCard fairId={fairId} />}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 items-center flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.emoji} {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              id="include-inactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-primary"
            />
            <label htmlFor="include-inactive" className="text-sm text-gray-700">
              Include inactive items
            </label>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Items List */}
      {items.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No rental items yet</h3>
          <p className="text-gray-600 mb-4">
            Start by adding equipment that vendors can rent for this fair.
          </p>
          <button
            onClick={handleCreateItem}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="w-5 h-5" />
            Add First Rental Item
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Availability
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item) => (
                <tr key={item.id} className={!item.is_active ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{getCategoryEmoji(item.category)}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        {item.description && (
                          <div className="text-sm text-gray-500">{item.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-moonbone text-ink">
                      {item.category || 'other'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {formatCurrency(item.price_per_day)}/day {/* Issue #905: Use string directly, no Number() conversion */}
                      </div>
                      {item.price_per_event && (
                        <div className="flex items-center gap-1 text-gray-500">
                          <DollarSign className="w-4 h-4" />
                          {formatCurrency(item.price_per_event)}/event {/* Issue #905: Use string directly, no Number() conversion */}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="flex items-center gap-1">
                        <Hash className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {item.quantity_available - item.quantity_reserved}
                        </span>
                        <span className="text-gray-500">/ {item.quantity_available} available</span>
                      </div>
                      {item.quantity_reserved > 0 && (
                        <div className="text-xs text-orange-600 mt-1">
                          {item.quantity_reserved} reserved
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.is_active ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEditItem(item)}
                        className="text-secondary hover:text-ink"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(item.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      <RentalItemForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingItem(null);
        }}
        fairId={fairId}
        item={editingItem || undefined}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="rental-delete-confirm-title"
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <h3
              id="rental-delete-confirm-title"
              className="text-lg font-medium text-gray-900 mb-4"
            >
              Confirm Deletion
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this rental item? This action cannot be undone.
              <br />
              <br />
              <strong className="text-red-600">
                Note: Items with existing reservations cannot be deleted. Set them to inactive instead.
              </strong>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteItem(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
